/**
 * Construction d'un DOM cible à partir d'un DOM source en minimisant au maximum les modifications du DOM cible
 * par évaluation différentielle des modificatons à réaliser dans la cible existante.
 * A utiliser par exemple dans des éditeurs avec refresh dynamique d'un aperçu HTML.
 *
 * Exemple de construction d'un HTML correpondant à un titre riche xml avec des balises récursives :
 * ```
 * 	new DomToDomUpdater(target, function (this: DomToDomUpdater, xml: Node) {
 * 		for (let ch = xml.firstChild; ch; ch = ch.nextSibling) {
 * 			if (ch instanceof Text) this.addText(ch.nodeValue);
 * 			else if (ch instanceof Element) this.addElt("span", {"class": ch.getAttribute("role")}, ch);
 * 		}
 * 	}).refresh(para);
 * ```
 *
 */
export class DomToDomUpdater {

	protected curParent: Node;
	protected curNextSib: Node;

	constructor(public root: Node, public buildRoot: (this: DomToDomUpdater, from: Node) => void) {}

	refresh(from: Node) {
		this.startRefresh();
		this.buildRoot(from);
		this.endRefresh();
	}

	/** Ajoute un noeud texte. */
	addText(txt: string) {
		if (!txt) return;
		if (this.curNextSib?.nodeType === Node.TEXT_NODE) {
			if (txt !== this.curNextSib.nodeValue) this.curNextSib.nodeValue = txt;
			this.curNextSib = this.curNextSib.nextSibling;
		} else {
			this.curParent.insertBefore(document.createTextNode(txt), this.curNextSib);
		}
	}

	/** Ajoute un élement cible et enclenche le traitement du from passé en paramètre dans ce nouvel élément cible. */
	addElt(name: string, atts: Dict<string>, subFrom?: Node | null, subBuild?: (this: DomToDomUpdater, xml: Node) => void) {
		this.enterElt(name, atts);
		if (subFrom) (subBuild || this.buildRoot).call(this, subFrom);
		this.exitElt();
	}

	/** Gestion manuelle d'entrée et sortie dans les tags cibles. Privillégier addElt() plus sécurisé. */
	enterElt(name: string, atts?: Dict<string>) {
		const current = this.curNextSib;
		if (current instanceof Element && current.nodeName === name) {
			const addedAtts = new Set();
			if (atts) {
				for (const k in atts) {
					const v = atts[k];
					if (v != null) {
						current.setAttribute(k, v);
						addedAtts.add(k);
					}
				}
			}
			for (const att of current.attributes) {
				if (!addedAtts.has(att.nodeName)) current.removeAttributeNode(att);
			}
			this.curParent = current;
			this.curNextSib = current.firstChild;
		} else {
			const elt = document.createElement(name);
			if (atts) for (const k in atts) {
				const v = atts[k];
				if (v != null) elt.setAttribute(k, v);
			}
			this.curParent = this.curParent.insertBefore(elt, current);
			this.curNextSib = null;
		}
	}

	/** Gestion manuelle d'entrée et sortie dans les tags cibles. Privillégier addElt() plus sécurisé. */
	exitElt() {
		if (this.curNextSib) {
			let node = this.curNextSib;
			while (node) {
				let next = node.nextSibling;
				node.parentNode.removeChild(node);
				node = next
			}
		}
		this.curNextSib = this.curParent.nextSibling;
		this.curParent = this.curParent.parentNode;
	}

	protected startRefresh(): this {
		this.curParent = this.root;
		this.curNextSib = this.root.firstChild;
		return this;
	}

	protected endRefresh() {
		if (this.curNextSib) {
			let node = this.curNextSib;
			while (node) {
				let next = node.nextSibling;
				node.parentNode.removeChild(node);
				node = next;
			}
		}
		this.curNextSib = null;
		this.curParent = null;
	}
}